import { KatanaPerpsFundingPayment } from '@katanaperps/katana-perps-sdk';
import React from 'react';

import { PAGE_LIMIT } from 'api/queries/fundingPaymentsQueries';
import PaginationNoContentOverWarning from 'components/basic/pagination/PaginationNoContentOverWarning';
import PaginationNumbers from 'components/basic/pagination/PaginationNumbers';
import BasicTableStackable from 'components/basic/table/BasicTableStackable';
import TextColored from 'components/basic/text/TextColored';
import { TableCoinIcon } from 'config/coinIcons/CoinIcon';
import { PAGINATION_PAGES_LIMIT } from 'config/constants';
import { formatDateTime } from 'lib/date';
import { formatNumber, formatUsdWithSign } from 'lib/display/formatting';
import { useSelector } from 'reduxState/store';

export default function FundingHistoryTable({
  walletAddress,
  pageItems,
  isLoading,
  showingPreviousPage,
  totalCount,
  page,
  onSetPage,
}: {
  walletAddress: string;
  pageItems: KatanaPerpsFundingPayment[] | undefined;
  showingPreviousPage: boolean;
  isLoading: boolean;
  totalCount: number | undefined;
  page: number;
  onSetPage: (page: number) => void;
}) {
  const isMobile = useSelector(state => state.screen.isMobile);
  const tickSizes = useSelector(state => state.markets.listedMarkets.tickSizes);
  const stepSizes = useSelector(state => state.markets.listedMarkets.stepSizes);

  return (
    <>
      {page > PAGINATION_PAGES_LIMIT ? (
        <PaginationNoContentOverWarning />
      ) : (
        <BasicTableStackable
          notFixedLayout
          getItemKey={item => `${item.market}-${item.time}`}
          items={pageItems || []}
          stripes
          marginBottom="24px"
          firstColumnPaddingLeft="16px"
          loading={!!walletAddress && (isLoading || showingPreviousPage)}
          errorText={
            !walletAddress
              ? 'Connect wallet to see your funding'
              : !pageItems
              ? '' // Loading
              : !pageItems.length
              ? 'No funding history'
              : undefined
          }
          columns={payment => {
            const [base, quote] = payment?.market?.split('-') || [];
            const {
              paymentQuantity,
              fundingRate,
              indexPrice,
              positionQuantity,
              time,
            } = payment || {};
            const paymentQuantityNr = Number(paymentQuantity);

            const quantityColored = (
              <TextColored
                color={Number(positionQuantity) < 0 ? 'red' : 'green'}
              >
                {formatNumber(positionQuantity, {
                  fixedDecimals: payment?.market
                    ? stepSizes[payment.market]?.visibleDecimals
                    : undefined,
                })}{' '}
                {base}
              </TextColored>
            );
            return [
              { name: 'Date', render: formatDateTime(time) },
              {
                name: 'Market',
                stackWithPrevious: isMobile,
                fontFamily: 'digits',
                render: (
                  <>
                    <TableCoinIcon symbol={base} />
                    {base}-{quote}
                  </>
                ),
              },
              {
                name: 'Funding Rate',
                stackWithPrevious: isMobile,
                render: fundingRate ? (
                  <TextColored
                    color={Number(fundingRate) < 0 ? 'red' : 'green'}
                  >
                    {formatNumber(Number(fundingRate) * 100, {
                      fixedDecimals: 6,
                    })}
                    %
                  </TextColored>
                ) : null,
              },
              /** Position (size and direction at the time of funding) in base currency */
              {
                name: 'Position',
                render: isMobile ? false : quantityColored,
              },
              {
                name: 'Index Price',
                render: formatNumber(indexPrice, {
                  fixedDecimals: payment?.market
                    ? tickSizes[payment.market]?.visibleDecimals
                    : undefined,
                }),
              },
              /** Funding rate for the funding payment/fee */
              {
                name: 'Funding Fee',
                stackWithPrevious: isMobile,
                render: (
                  <TextColored color={paymentQuantityNr < 0 ? 'red' : 'green'}>
                    {formatUsdWithSign(paymentQuantityNr, { decimals: 4 })}
                  </TextColored>
                ),
              },
              {
                name: 'Position',
                render: isMobile ? quantityColored : false,
                align: 'right',
              },
            ];
          }}
        />
      )}
      {!isLoading && (
        <PaginationNumbers
          onSelect={onSetPage}
          pageLimit={PAGE_LIMIT}
          selected={page}
          totalCount={totalCount}
        />
      )}
    </>
  );
}
